import React, { useMemo } from 'react';
import { toCurrency } from '@/helpers/masks/toCurrency';

export interface SummaryCardItem {
  id?: string;
  name?: string;
  serialNumber?: string;
  summary?: {
    amountSold?: number;
    courtesies?: number;
    discount?: number;
    totalSaleValue?: number;
    totalCommission?: number;
    totalSaleWithCommission?: number;
    initialInventory?: number;
    availableStock?: number;
  };
  payments?: {
    credit?: number;
    debit?: number;
    pix?: number;
    money?: number;
    courtesy?: number;
  };
}

export interface SaleProductSummaryCardProps {
  title: string;
  items: SummaryCardItem[];
  totalItemsCount?: number;
  unitName?: { singular: string; plural: string };
  isFiltered?: boolean;
  payments?: {
    credit?: number;
    debit?: number;
    pix?: number;
    money?: number;
    bankSlip?: number;
  };
  customCourtesies?: number;
}

export const SaleProductSummaryCard: React.FC<SaleProductSummaryCardProps> = ({
  title,
  items,
  totalItemsCount,
  unitName,
  isFiltered = false,
  payments: paymentsProp,
  customCourtesies,
}) => {
  const totals = useMemo(
    () =>
      items.reduce(
        (acc, item) => {
          const s = item.summary;
          const p = item.payments;

          const amountSold = Number(s?.amountSold || 0);
          const courtesies = Number(s?.courtesies || 0);
          const discount = Number(s?.discount || 0);
          const totalSaleValue = Number(s?.totalSaleValue || 0);
          const totalCommission = Number(s?.totalCommission || 0);
          const totalSaleWithCommission = Number(
            s?.totalSaleWithCommission ?? totalSaleValue + totalCommission,
          );

          const credit = Number(p?.credit || 0);
          const debit = Number(p?.debit || 0);
          const pix = Number(p?.pix || 0);
          const money = Number(p?.money || 0);

          return {
            amountSold: acc.amountSold + amountSold,
            courtesies: acc.courtesies + courtesies,
            discount: acc.discount + discount,
            totalSaleValue: acc.totalSaleValue + totalSaleValue,
            totalCommission: acc.totalCommission + totalCommission,
            totalSaleWithCommission: acc.totalSaleWithCommission + totalSaleWithCommission,
            credit: acc.credit + credit,
            debit: acc.debit + debit,
            pix: acc.pix + pix,
            money: acc.money + money,
          };
        },
        {
          amountSold: 0,
          courtesies: 0,
          discount: 0,
          totalSaleValue: 0,
          totalCommission: 0,
          totalSaleWithCommission: 0,
          credit: 0,
          debit: 0,
          pix: 0,
          money: 0,
        },
      ),
    [items],
  );

  const averageTicket = useMemo(
    () => (totals.amountSold > 0 ? totals.totalSaleValue / totals.amountSold : 0),
    [totals.amountSold, totals.totalSaleValue],
  );

  const countText = useMemo(() => {
    const sName = unitName?.singular || 'item';
    const pName = unitName?.plural || 'itens';
    const count = items.length;
    const name = count === 1 ? sName : pName;

    if (totalItemsCount && totalItemsCount > count) {
      return `${count} ${name} de ${totalItemsCount}`;
    }
    return `${count} ${name}`;
  }, [items.length, totalItemsCount, unitName]);

  const payCredit = Number(paymentsProp?.credit ?? totals.credit);
  const payDebit = Number(paymentsProp?.debit ?? totals.debit);
  const payPix = Number(paymentsProp?.pix ?? totals.pix);
  const payMoney = Number(paymentsProp?.money ?? totals.money);
  const payBankSlip = Number(paymentsProp?.bankSlip ?? 0);
  const payTotal = payCredit + payDebit + payPix + payMoney + payBankSlip;

  if (items.length === 0 && payTotal === 0) {
    return null;
  }

  const metrics = [
    { title: 'Qtd. Vendas', content: totals.amountSold },
    { title: 'Cortesias', content: customCourtesies ?? totals.courtesies },
    { title: 'Descontos', content: toCurrency(totals.discount) },
    { title: 'Ticket Médio', content: toCurrency(averageTicket) },
    { title: 'Total Vendas', content: toCurrency(totals.totalSaleValue), highlight: true },
    { title: 'Comissão', content: toCurrency(totals.totalCommission) },
    {
      title: 'Vendas + Comissão',
      content: toCurrency(totals.totalSaleWithCommission),
      highlight: true,
    },
  ];

  const paymentItems = [
    { title: 'Cartão de Crédito', content: toCurrency(payCredit) },
    { title: 'Cartão de Débito', content: toCurrency(payDebit) },
    { title: 'PIX', content: toCurrency(payPix) },
    { title: 'Dinheiro', content: toCurrency(payMoney) },
    ...(payBankSlip > 0 ? [{ title: 'Boleto / Outros', content: toCurrency(payBankSlip) }] : []),
  ];

  return (
    <div
      style={{
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '20px 24px',
        marginBottom: '24px',
        border: '1px solid #e9ecef',
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)',
      }}
    >
      {/* Cabeçalho do Card de Resumo */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '8px',
          marginBottom: '16px',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h6
            style={{
              fontSize: '14px',
              fontWeight: 600,
              color: '#495057',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              margin: 0,
            }}
          >
            {title}
          </h6>
          {isFiltered && (
            <span
              style={{
                backgroundColor: '#e8f4fd',
                color: '#1b4880',
                border: '1px solid #b6d4fe',
                fontSize: '11px',
                fontWeight: 600,
                padding: '2px 8px',
                borderRadius: '12px',
              }}
            >
              Filtrado
            </span>
          )}
        </div>
        <span
          style={{
            fontSize: '12.5px',
            color: '#6c757d',
            fontWeight: 500,
          }}
        >
          {countText}
        </span>
      </div>

      {/* Grid de Métricas Gerais de Venda */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        {metrics.map((item, idx) => (
          <div
            key={`metric-${idx}`}
            style={{
              backgroundColor: '#f8f9fa',
              borderRadius: '10px',
              padding: '12px 14px',
              border: '1px solid #f1f3f5',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <span
              style={{
                fontSize: '11px',
                fontWeight: 600,
                color: '#6c757d',
                textTransform: 'uppercase',
                marginBottom: '4px',
              }}
            >
              {item.title}
            </span>
            <span
              style={{
                fontSize: '15px',
                fontWeight: 600,
                color: item.highlight ? '#1b4880' : '#343a40',
              }}
            >
              {item.content}
            </span>
          </div>
        ))}
      </div>

      {/* Grid de Meios de Pagamento */}
      <div
        style={{
          borderTop: '1px solid #f1f3f5',
          paddingTop: '14px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
          }}
        >
          <span
            style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#6c757d',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Vendas por Meio de Pagamento
          </span>
          {payTotal > 0 && (
            <span style={{ fontSize: '12px', color: '#1b4880', fontWeight: 600 }}>
              Total: {toCurrency(payTotal)}
            </span>
          )}
        </div>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))',
            gap: '10px',
          }}
        >
          {paymentItems.map((pay, idx) => (
            <div
              key={`payment-${idx}`}
              style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '10px 14px',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <span style={{ fontSize: '11px', color: '#6c757d', marginBottom: '3px' }}>
                {pay.title}
              </span>
              <strong style={{ fontSize: '14px', color: '#212529', fontWeight: 600 }}>
                {pay.content}
              </strong>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
